import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import classes from './roomHeader.module.scss';

interface RoomHeaderProps {
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
  color?: string;
  roomId?: number;
  isPrinting?: boolean;
}

const RoomHeader: React.FC<RoomHeaderProps> = observer(
  ({ name, isExpanded, onToggle, color, roomId, isPrinting }) => {
    const { roomStore } = useStore();
    const [roomColors, setRoomColors] = useState<Record<string, string>>({
      Purple: '#663399',
      Green: '#3CB371',
      Blue: '#0000FF',
      Gold: '#FFD700',
      Red: '#d32f2f',
      Silver: '#C0C0C0',
      White: '#f5f5f5',
      Black: '#000000',
      RedB: '#8B0000',
      RedC: '#FF0000',
      RedD: '#DC143C',
      WhiteA: '#F5F5F5',
      WhiteB: '#FFFFFF',
      Saps: '#9932CC'
    });

    // State to store formatted room display names
    const [formattedRoomNames, setFormattedRoomNames] = useState<Record<string, string>>({});

    // Generate a deterministic color for rooms not in our mapping
    const generateRandomColor = (): string => {
      // Use a predefined set of colors to avoid Math.random() security concern
      const predefinedColors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
        '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
        '#A9CCE3', '#F9E79F', '#D5A6BD', '#A2D9CE', '#FAD7A0'
      ];

      // Use room name hash to deterministically select a color
      const hash = name.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);

      const index = Math.abs(hash) % predefinedColors.length;
      return predefinedColors[index];
    };

    // Initialize room data when roomStore changes
    useEffect(() => {
      if (roomStore.rooms && roomStore.rooms.length > 0) {
        const updatedColors = { ...roomColors };
        const updatedRoomNames = { ...formattedRoomNames };

        roomStore.rooms.forEach(room => {
          const roomLong = room.room_long;
          const roomShort = room.room_short;

          // Add random color for new rooms
          if (roomLong && !updatedColors[roomLong]) {
            updatedColors[roomLong] = generateRandomColor();
          }

          // Create formatted room name (PURPLE - PU)
          if (roomLong && roomShort) {
            // Format as uppercase room name - short code
            updatedRoomNames[roomLong] = `${roomLong.toUpperCase()} - ${roomShort}`;
            // Also map by ID for easier lookup
            if (room.id) {
              updatedRoomNames[`id_${room.id}`] = `${roomLong.toUpperCase()} - ${roomShort}`;
            }
          }
        });

        setRoomColors(updatedColors);
        setFormattedRoomNames(updatedRoomNames);
      }
    }, [roomStore.rooms]);

    // Get room color from our mapping
    const getRoomColor = (roomName: string): string => {
      // If color prop is provided, use it
      if (color) {
        return color;
      }

      // Try to find the room in our mapping - handle both exact matches and partial matches
      let matchedColor = roomColors[roomName];

      if (!matchedColor) {
        // Try to find partial matches for room names
        const matchedKey = Object.keys(roomColors).find(
          key =>
            roomName.toLowerCase().includes(key.toLowerCase()) ||
            key.toLowerCase().includes(roomName.toLowerCase())
        );
        matchedColor = matchedKey ? roomColors[matchedKey] : '#4d2e6d';
      }

      return matchedColor;
    };

    // Get formatted room name
    const getDisplayName = (): string => {
      // First try finding by room ID if provided
      if (roomId && formattedRoomNames[`id_${roomId}`]) {
        return formattedRoomNames[`id_${roomId}`];
      }

      // Then try by room name
      if (formattedRoomNames[name]) {
        return formattedRoomNames[name];
      }

      // Find matching room in roomStore
      const matchedRoom = roomStore.rooms.find(
        room => room.room_long === name || room.room_short === name
      );

      if (matchedRoom) {
        return `${matchedRoom.room_long.toUpperCase()} - ${matchedRoom.room_short}`;
      }

      // Fallback to original name if no match found
      return name.toUpperCase();
    };

    return (
      <div className={classes.roomHeader} onClick={onToggle}>
        {!isPrinting && <span className={classes.roomIndicator} style={{ backgroundColor: getRoomColor(name) }} />}
        <div className={classes.roomTitle}>
          <span className={classes.roomName}>{getDisplayName()}</span>
          {!isPrinting && (
            <span
              style={{
                display: 'inline-block',
                fontSize: '12px',
                transition: 'transform 0.2s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
              className={`${classes.expandIcon} ${isExpanded ? classes.expanded : ''}`}
            >
              ▼
            </span>
          )}
        </div>
      </div>
    );
  }
);

export default RoomHeader;
